import { BridgeComponent } from '@hotwired/hotwire-native-bridge'
import { visitProfileTab } from '../../turbo/in_place_visit'

// Gestes de la barre d'onglets native, joués PAR LA PAGE. Le composant est posé
// sur le <body> de toutes les pages natives (body_bridge_attributes) : le natif
// a donc toujours quelqu'un à qui parler, y compris sur une page sans onglets.
//
// Le natif garde ce qui lui appartient — pile de navigation, focus du champ de
// recherche, haptique, et sur iOS le défilement (son UIScrollView a un inset
// sous la nav bar, `window.scrollTo` ne tomberait pas au même endroit). Ce qui
// vit ici, c'est le DOM : quel onglet de page cliquer, quelle nav remonter.
// Avant, chaque plateforme injectait un script qui connaissait ce DOM — un
// renommage de ce côté cassait le geste sans la moindre erreur, nulle part.
//
// CONTRAT — le natif répond au connect avec les étapes qu'il veut, DANS L'ORDRE.
// La page exécute la première qui s'applique et renvoie `{ handled }`, pour que
// le natif sache s'il doit prendre le relais :
//
//   { steps: ['scroll', 'tab', 'reset'] }  re-tap Android (trois marches)
//   { steps: ['tab'] }                     re-tap iOS (il défile lui-même)
//   { steps: ['reset'] }                   on quitte/rejoint l'onglet du profil
//
// Chaque plateforme compose ainsi sa cascade — elles diffèrent à dessein — sans
// que ce contrôleur ait à savoir laquelle lui parle.
export default class extends BridgeComponent {
  static component = 'tab-reselect'
  // Le panneau des sous-onglets du profil, sur les seules pages qui en ont un.
  static targets = ['panel']

  connect() {
    super.connect()
    this.#sendConnect()
    // Onglet préchargé hors écran : le connect part destination inactive, le
    // bridge l'ignore, et la page ne répondrait plus à aucun geste. On rejoue à
    // chaque apparition — même recette que bridge--button, l'app émettant
    // `native:appeared` à la sélection d'onglet.
    this.appearedHandler = () => this.#sendConnect()
    window.addEventListener('native:appeared', this.appearedHandler)
  }

  disconnect() {
    window.removeEventListener('native:appeared', this.appearedHandler)
    super.disconnect()
  }

  // Tap sur un sous-onglet du profil (action posée sur le lien, natif seulement).
  // Le lien n'est pas suivi : la visite part d'ici, par le même chemin que la
  // marche `reset` (cf. `turbo/in_place_visit`), sans l'aller-retour de proposition
  // JS -> natif -> JS — latence sensible au tap, et écran remplacé à l'arrivée.
  //
  // Le preventDefault arrive à temps : Turbo décide dans la phase de BULLE, sur
  // le document (`LinkClickObserver#clickBubbled`, qui écarte les clics
  // `defaultPrevented`), et une action posée sur le lien bulle avant lui. Rien
  // n'est arrêté au passage : l'haptique du lien, autre action du même clic, tire
  // quand même.
  tabClick(event) {
    const link = event.currentTarget
    if (link.getAttribute('aria-current') === 'page') return

    event.preventDefault()
    visitProfileTab(link, this.#panel)
  }

  // Nul sur une page sans panneau — toute page hors profil.
  get #panel() {
    return this.hasPanelTarget ? this.panelTarget : null
  }

  #sendConnect() {
    this.send('connect', {}, ({ data: { steps } }) => {
      const handled = (steps || []).some((step) => this.#perform(step))
      this.send('performed', { handled })
    })
  }

  #perform(step) {
    switch (step) {
      case 'tab':
        return this.#clickPageTab()
      case 'scroll':
        return this.#scrollToTop()
      case 'reset':
        return this.#resetSubTab()
      default:
        return false
    }
  }

  // Onglets DE LA PAGE (catégories des tendances) : retour au premier, ce qui
  // rejoue ses animations. Le PREMIER onglet, pas le premier non sélectionné :
  // déjà dessus, l'étape ne s'applique pas et la suivante prend la main.
  #clickPageTab() {
    const tab = document.querySelector('[data-tabs-target="tab"]')
    if (!tab || tab.getAttribute('aria-selected') === 'true') return false
    tab.click()
    return true
  }

  #scrollToTop() {
    if (window.scrollY <= 0) return false
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return true
  }

  // Premier lien de la nav des sous-onglets du profil, s'il n'est pas déjà le
  // courant — sur toute autre page la nav n'existe pas, et il n'y a rien à
  // faire. La MÊME visite que le tap sur l'onglet (`turbo/in_place_visit`), pas
  // un clic : le clic déclencherait en plus l'haptique du lien, un second tic
  // sur un geste dont la barre d'onglets a déjà donné le sien.
  #resetSubTab() {
    const first = document.querySelector('nav[data-turbo-prefetch] a[href]')
    if (!first || first.getAttribute('aria-current') === 'page') return false
    visitProfileTab(first, this.#panel, { keepScroll: false })
    return true
  }
}
